'use client'

import { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Link,
  VStack,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { useAppToast } from '@/hooks/useAppToast'

interface LoginFormProps {
  onSuccess: () => void
}

export default function LoginForm({ onSuccess }: LoginFormProps) {
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useAppToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    const result = await login(email, password)
    setLoading(false)
    if (result.ok) {
      onSuccess()
      return
    }
    const messages: Record<string, { title: string; description?: string; duration?: number }> = {
      invalid: { title: 'Invalid email or password', duration: 3000 },
      locked: {
        title: 'Too many failed sign-in attempts. Try again later.',
        duration: 6000,
      },
      throttled: {
        title: 'Too many sign-in attempts from this network. Try again shortly.',
        duration: 5000,
      },
      validation: { title: 'Enter a valid email and password.', duration: 4000 },
      unavailable: { title: 'Sign-in is temporarily unavailable. Try again later.', duration: 5000 },
      timeout: {
        title: 'Connection timed out',
        description: 'Sign-in is taking longer than expected on this network. Wait a moment and try again.',
        duration: 6000,
      },
      unknown: { title: 'Something went wrong. Try again.', duration: 4000 },
    }
    const m = messages[result.reason]
    toast({
      title: m.title,
      description: m.description,
      status: 'error',
      duration: m.duration ?? 4000,
    })
  }

  return (
    <Box as="form" onSubmit={handleSubmit} w="full" maxW="md">
      <VStack spacing={4}>
        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
          />
        </FormControl>
        <Box w="full" textAlign="right">
          <Link as={NextLink} href="/forgot-password" fontSize="sm" color="brand.500">
            Forgot password?
          </Link>
        </Box>
        <Button type="submit" colorScheme="brand" w="full" isLoading={loading}>
          Sign in
        </Button>
      </VStack>
    </Box>
  )
}
